import classNames from 'classnames';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import React, { useState } from 'react';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { SITE_LINKS } from '../../constants';
import { GoogleAnalyticsEvents } from '../../enum/GoogleAnalyticsEvents';
import { AuthService } from '../../services/auth';
import { Background } from '../../stories/Background';
import { Button } from '../../stories/Button';
import { InputFeedback } from '../../stories/InputFeedback';
import { Link } from '../../stories/Link';
import { LoginCard } from '../../stories/LoginCard';
import { Panel } from '../../stories/Panel';
import { TextInput } from '../../stories/TextInput';

export interface LoginPageProps {
  data?: {
    csrfToken: string;
  };
}

export type LoginData = { username: string; password: string };

export const LoginPage: React.FC<LoginPageProps> = (props) => {
  const __ = useTranslation().t;
  const [focusFieldName, setFocusFieldName] = useState<string | null>(null);
  const [errorStateActive, setErrorStateActive] = useState<boolean>(false);
  const csrfToken = props.data?.csrfToken;
  const initialValues: LoginData = { username: '', password: '' };

  const schema = yup.object().shape({
    username: yup.string().required(__('fieldRequired')),
    password: yup.string().required(__('fieldRequired')),
  });

  const onSubmit = (values: LoginData, { setSubmitting }: FormikHelpers<LoginData>) => {
    ReactGA.event(GoogleAnalyticsEvents.LoginRequest);
    AuthService.postLogin({
      ...values,
      _token: csrfToken,
    })
      .then((response) => {
        if (response.request.responseURL) {
          window.location.href = response.request.responseURL;
        } else {
          window.location.href = SITE_LINKS.HomePage;
        }
      })
      .catch(() => {
        ReactGA.event(GoogleAnalyticsEvents.LoginError);
        setErrorStateActive(true);
        setSubmitting(false);
      });
  };

  return (
    <Background>
      <div
        className={classNames('oejr-page-login', errorStateActive && 'oejr-page-login-error-state')}
      >
        <LoginCard showCharacters showTag>
          <Formik initialValues={initialValues} validationSchema={schema} onSubmit={onSubmit}>
            {({ isSubmitting, values }) => (
              <Form>
                <Panel
                  variant="danger"
                  hidden={!errorStateActive}
                  onHide={() => setErrorStateActive(false)}
                >
                  <p>{__('opsCheckTyposContactUs')}</p>
                </Panel>
                <Field
                  type="text"
                  name="username"
                  placeholder={(focusFieldName !== 'username' && __('username')) || ''}
                  label={(focusFieldName === 'username' || values.username) && __('username')}
                  onFocus={() => setFocusFieldName('username')}
                  onBlur={() => setFocusFieldName(null)}
                  component={TextInput}
                  maxLength={80}
                />
                <ErrorMessage
                  name="username"
                  component={({ children }) => (
                    <InputFeedback state="error" text={children} icon="exclamation-circle" />
                  )}
                />
                <Link
                  href={SITE_LINKS.ForgotUsernamePage}
                  onClick={() => {}}
                  variant="blue-main"
                  italic={false}
                >
                  {__('forgotYourUsername')}
                </Link>
                <Field
                  type="password"
                  name="password"
                  placeholder={(focusFieldName !== 'password' && __('password')) || ''}
                  label={(focusFieldName === 'password' || values.password) && __('password')}
                  onFocus={() => setFocusFieldName('password')}
                  onBlur={() => setFocusFieldName(null)}
                  component={TextInput}
                />
                <ErrorMessage
                  name="password"
                  component={({ children }) => (
                    <InputFeedback state="error" text={children} icon="exclamation-circle" />
                  )}
                />
                <Link
                  href={SITE_LINKS.ResetPasswordPage}
                  onClick={() => {}}
                  variant="blue-main"
                  italic={false}
                >
                  {__('forgotYourPassword')}
                </Link>
                <Button
                  id="oejr-login-submit-button"
                  label={__('login')}
                  type="submit"
                  disabled={isSubmitting}
                  variant="primary"
                />
              </Form>
            )}
          </Formik>
        </LoginCard>
      </div>
    </Background>
  );
};
